import React, { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Box,
  Button,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  OutlinedInput,
  InputAdornment,
  NativeSelect,
  FormHelperText,
  CircularProgress,
  Paper,
} from '@mui/material';
import RevertToDraftDialog from './RevertToDraftDialog';
import SowAddendumFormDialog from './SowAddendumFormDialog';
import SowAddendumRequiredDialog from './SowAddendumRequiredDialog';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import toast from 'react-hot-toast';
import RTEditor from 'components/RTEditor';
import NumberInput from 'components/NumberInput';
import { CustomNativeSelect } from 'components/Styled';
import httpClient from 'utils/httpClient';
import { getApiErrorPayload } from 'utils/apiError';
import { formHelperTextCss } from 'views/Clients/GigForm/styles';
import { milestoneSchema } from './schema';

const MilestoneForm = ({
  sowId,
  sow,
  onSuccess,
  onCancel,
  existingMilestones = [],
  milestone = null, // For editing existing milestone
  onSowUpdate, // Callback to update sow state in parent
  onDocumentsReset,
}) => {
  const [loading, setLoading] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertLoading, setRevertLoading] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [addendumRequiredOpen, setAddendumRequiredOpen] = useState(false);
  const [addendumFormOpen, setAddendumFormOpen] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(milestoneSchema),
    mode: 'onChange', // Validate on change and clear errors immediately
    reValidateMode: 'onChange', // Re-validate on change to clear errors when validation passes
    defaultValues: {
      title: milestone?.title || '',
      description: (() => {
        // Handle Lexical editor state object from backend
        if (!milestone?.description) return '';

        // If it's already a string (HTML), use it directly
        if (typeof milestone.description === 'string') {
          return milestone.description;
        }

        // If it's a Lexical state object, serialize it to the RTEditor format
        if (
          typeof milestone.description === 'object' &&
          milestone.description.root
        ) {
          return JSON.stringify(milestone.description);
        }

        return '';
      })(),
      duration: milestone?.duration || null,
      duration_unit: milestone?.duration_unit || 'days',
      start_date: milestone?.start_date ? new Date(milestone.start_date) : null,
      end_date: milestone?.end_date ? new Date(milestone.end_date) : null,
      estimated_hours: milestone?.estimated_hours || null,
    },
  });

  const watchedValues = watch();

  // Calculate milestone amounts based on form values and SOW data
  const { estimated_hours, duration, duration_unit } = watchedValues;
  const milestoneAmounts = useMemo(() => {
    // Step 1: Calculate hours
    let hours = null;
    if (
      estimated_hours !== null &&
      estimated_hours !== undefined &&
      estimated_hours !== ''
    ) {
      hours = Number(estimated_hours);
    } else if (
      duration !== null &&
      duration !== undefined &&
      duration !== '' &&
      duration_unit
    ) {
      // Calculate hours from duration + duration_unit
      const durationNum = Number(duration);
      switch (duration_unit) {
        case 'days':
          hours = durationNum * 8;
          break;
        case 'weeks':
          hours = durationNum * 40;
          break;
        case 'months':
          hours = durationNum * 160;
          break;
        default:
          hours = null;
      }
    }

    if (!hours || Number.isNaN(hours) || hours <= 0) {
      return {
        hours: null,
        hourlyRate: null,
        freelancerAmount: null,
        clientAmount: null,
        platformRevenue: null,
        markupPercent: null,
        fxRate: sow?.fx_rate || null,
      };
    }

    // Step 2: Get hourly rate
    // Priority: sow.metadata["freelancer_rate"] > freelancer.begig_set_rate
    let hourlyRate = null;
    if (
      sow?.metadata?.freelancer_rate !== undefined &&
      sow?.metadata?.freelancer_rate !== null
    ) {
      hourlyRate = Number(sow.metadata.freelancer_rate);
    } else if (
      sow?.freelancer?.begig_set_rate !== undefined &&
      sow?.freelancer?.begig_set_rate !== null
    ) {
      hourlyRate = Number(sow.freelancer.begig_set_rate);
    }

    if (!hourlyRate || Number.isNaN(hourlyRate) || hourlyRate <= 0) {
      return {
        hours,
        hourlyRate: null,
        freelancerAmount: null,
        clientAmount: null,
        platformRevenue: null,
        markupPercent: null,
        fxRate: sow?.fx_rate || null,
      };
    }

    // Step 3: Calculate freelancer_payout = ceil(hourly_rate × hours)
    const freelancerAmount = Math.ceil(hourlyRate * hours);

    // Step 4: Calculate billable_amount
    // Get markup_percent from sow.markup_percent (root level, not in metadata)
    const markupPercent = sow?.markup_percent;
    if (markupPercent === undefined || markupPercent === null) {
      return {
        hours,
        hourlyRate,
        freelancerAmount,
        clientAmount: null,
        platformRevenue: null,
        markupPercent: null,
        fxRate: sow?.fx_rate || null,
      };
    }

    const markupMultiplier = 1 + Number(markupPercent) / 100;
    const baseClientAmount = freelancerAmount * markupMultiplier;
    const clientAmount = Math.floor(baseClientAmount);
    const platformRevenue = clientAmount - freelancerAmount;

    return {
      hours,
      hourlyRate,
      freelancerAmount,
      clientAmount,
      platformRevenue,
      markupPercent: Number(markupPercent),
      fxRate: sow?.fx_rate || null,
    };
  }, [estimated_hours, duration, duration_unit, sow]);

  // Helper to format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '—';
    }
    return `₹${Number(value).toLocaleString('en-IN')}`;
  };

  // Helper to ensure description is always a string in form state
  const normalizeDescription = React.useCallback((val) => {
    if (typeof val === 'string') return val;
    if (val && typeof val === 'object') {
      try {
        return JSON.stringify(val);
      } catch {
        return '';
      }
    }
    return '';
  }, []);

  const lexicalHasText = React.useCallback((input) => {
    if (!input) return false;

    const hasTextNodes = (nodes) => {
      for (const node of nodes || []) {
        if (
          node.type === 'text' &&
          typeof node.text === 'string' &&
          node.text.trim().length > 0
        ) {
          return true;
        }
        if (Array.isArray(node.children) && hasTextNodes(node.children)) {
          return true;
        }
      }
      return false;
    };

    if (typeof input === 'string') {
      const trimmed = input.trim();
      if (!trimmed) return false;

      try {
        const parsed = JSON.parse(trimmed);
        if (parsed?.root?.children) {
          return hasTextNodes(parsed.root.children);
        }
      } catch {
        return trimmed.length > 0;
      }
      return false;
    }

    if (typeof input === 'object' && input?.root?.children) {
      return hasTextNodes(input.root.children);
    }

    return false;
  }, []);

  // Coerce description into string if external/reset sets an object or empty Lexical state
  React.useEffect(() => {
    const v = watchedValues.description;

    if (v && typeof v === 'object') {
      setValue('description', normalizeDescription(v), {
        shouldValidate: false,
        shouldDirty: false,
      });
      return;
    }

    if (typeof v === 'string' && v) {
      if (!lexicalHasText(v)) {
        setValue('description', '', {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
    }
  }, [
    watchedValues.description,
    normalizeDescription,
    lexicalHasText,
    setValue,
  ]);

  // Auto-calculate suggested end date based on start date and duration
  React.useEffect(() => {
    const { start_date, duration, duration_unit } = watchedValues;
    if (start_date && duration && duration_unit && !watchedValues.end_date) {
      const startMoment = moment(start_date);
      let endMoment;

      switch (duration_unit) {
        case 'days':
          endMoment = startMoment.clone().add(duration, 'days');
          break;
        case 'weeks':
          endMoment = startMoment.clone().add(duration, 'weeks');
          break;
        case 'months':
          endMoment = startMoment.clone().add(duration, 'months');
          break;
        default:
          return;
      }

      setValue('end_date', endMoment.toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedValues.start_date,
    watchedValues.duration,
    watchedValues.duration_unit,
    watchedValues.end_date,
  ]);

  // Validate date continuity
  const validateDateContinuity = (startDate, endDate) => {
    // Filter out current milestone when editing to avoid self-overlap
    const milestonesToCheck = milestone
      ? existingMilestones.filter((m) => m.id !== milestone.id)
      : existingMilestones;

    if (milestonesToCheck.length === 0) return true;

    const newStart = moment(startDate);
    const newEnd = moment(endDate);

    for (const existingMilestone of milestonesToCheck) {
      const existingStart = moment(existingMilestone.start_date);
      const existingEnd = moment(existingMilestone.end_date);

      // Check for overlaps
      if (
        newStart.isBetween(existingStart, existingEnd, null, '[]') ||
        newEnd.isBetween(existingStart, existingEnd, null, '[]') ||
        existingStart.isBetween(newStart, newEnd, null, '[]') ||
        existingEnd.isBetween(newStart, newEnd, null, '[]')
      ) {
        return false;
      }
    }
    return true;
  };

  const handleRevertToDraft = async () => {
    if (!sowId) {
      toast.error('SOW ID is missing');
      return;
    }

    setRevertLoading(true);

    try {
      const response = await httpClient.post(
        `/api/v1/project-management/sows/${sowId}/revert-to-draft/`
      );

      if (response?.status === false) {
        throw new Error(
          response?.message || 'Failed to revert SOW to draft status'
        );
      }

      toast.success(response?.message || 'SOW reverted to DRAFT successfully');

      // Update sow state: set documents to null and update status
      if (onSowUpdate) {
        onSowUpdate({
          status: 'draft',
          client_document: null,
          freelancer_document: null,
          document_context: {
            action: 'generate_sow',
            has_signed_sow: false,
            has_draft_sow: true,
            has_draft_addendum: false,
            draft_sow_id: sowId || null,
            draft_addendum_id: null,
          },
          ...(response?.data || {}),
        });
      }

      onDocumentsReset?.();

      // Close the dialog
      setRevertDialogOpen(false);

      // Retry the form submission with pending data
      if (pendingFormData) {
        await submitFormData(pendingFormData);
        setPendingFormData(null);
      }
    } catch (error) {
      console.error('Error reverting SOW to draft:', error);
      toast.error(error?.message || 'Failed to revert SOW to draft status');
    } finally {
      setRevertLoading(false);
    }
  };

  const submitFormData = async (data) => {
    setLoading(true);
    try {
      const formValues = data;

      const payload = {
        ...formValues,
        start_date: moment(formValues.start_date).format('YYYY-MM-DD'),
        end_date: moment(formValues.end_date).format('YYYY-MM-DD'),
        duration: formValues.duration,
        estimated_hours: formValues.estimated_hours,
      };

      let response;

      if (milestone) {
        // Edit existing milestone
        response = await httpClient.patch(
          `api/v1/project-management/milestones/${milestone.id}/`,
          payload
        );

        if (response?.status === false) {
          throw new Error(response?.message || 'Failed to update milestone');
        }

        toast.success(response?.message || 'Milestone updated successfully');
      } else {
        // Create milestone for project
        response = await httpClient.post(
          `/api/v1/project-management/projects/${sow?.project_id}/milestones/`,
          payload
        );

        if (response?.status === false) {
          throw new Error(response?.message || 'Failed to create milestone');
        }

        toast.success(response?.message || 'Milestone created successfully');
      }
      onSuccess?.();
    } catch (error) {
      console.error(
        `Error ${milestone ? 'updating' : 'creating'} milestone:`,
        error
      );

      const { code: errorCode, message: errorMessage } =
        getApiErrorPayload(error);

      if (errorCode === 'SOW_ACTIVE_REVERT_REQUIRED') {
        // Store the form data and show revert dialog
        setPendingFormData(data);
        setRevertDialogOpen(true);
        return;
      }

      if (errorCode === 'SOW_SIGNED_ADDENDUM_REQUIRED') {
        toast.error(
          errorMessage || `Failed to ${milestone ? 'update' : 'create'} milestone`
        );
        return;
      }

      toast.error(
        errorMessage || `Failed to ${milestone ? 'update' : 'create'} milestone`
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    // Validate that milestone dates don't overlap with existing milestones
    if (!validateDateContinuity(data.start_date, data.end_date)) {
      toast.error('Milestone dates cannot overlap with existing milestones');
      return;
    }

    await submitFormData(data);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Typography
        variant='h6'
        sx={{ mb: 3, fontSize: '1.125rem', fontWeight: 600 }}
      >
        {milestone ? 'Edit Milestone' : 'Create New Milestone'}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  fontSize: '14px',
                  marginBottom: 0.5,
                  paddingLeft: 0.5,
                  fontWeight: 500,
                  color: '#4A5264',
                }}
              >
                Title
              </FormLabel>
              <Controller
                name='title'
                control={control}
                render={({ field }) => (
                  <OutlinedInput
                    {...field}
                    placeholder='e.g. Backend API Development'
                    sx={{
                      height: '44px',
                      borderColor: '#E5E7EB',
                      borderRadius: '6px',
                    }}
                  />
                )}
              />
              {errors?.title && (
                <FormHelperText sx={{ color: '#d32f2f', fontSize: '0.75rem' }}>
                  {errors.title.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  fontSize: '14px',
                  marginBottom: 0.5,
                  paddingLeft: 0.5,
                  fontWeight: 500,
                  color: '#4A5264',
                }}
              >
                Description
              </FormLabel>
              <Controller
                name='description'
                control={control}
                render={({ field, fieldState }) => {
                  const normalizedValue = normalizeDescription(field.value);
                  return (
                    <FormControl fullWidth sx={{ marginBottom: 3 }}>
                      <RTEditor
                        value={normalizedValue}
                        onChange={(v) =>
                          field.onChange(normalizeDescription(v))
                        }
                        placeholder='Add Milestone Deliverables'
                        name='description'
                        minRows={4}
                        maxRows={8}
                        features={{ link: false, code: false }}
                        error={!!fieldState?.error}
                      />
                      {!!fieldState?.error && (
                        <FormHelperText sx={formHelperTextCss}>
                          {fieldState?.error?.message?.toString()}
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
            </FormControl>
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  fontSize: '14px',
                  marginBottom: 0.5,
                  paddingLeft: 0.5,
                  fontWeight: 500,
                  color: '#4A5264',
                }}
              >
                Start Date
              </FormLabel>
              <Controller
                name='start_date'
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? moment(field.value) : null}
                    onChange={(val) =>
                      field.onChange(
                        val ? (val.toDate?.() ? val.toDate() : val) : null
                      )
                    }
                    format='DD/MM/YYYY'
                    shouldDisableDate={(day) => {
                      const d = day.startOf('day');
                      const today = moment().startOf('day');
                      const threeMonthsFromNow = moment()
                        .add(3, 'months')
                        .startOf('day');
                      // Disable past dates (before today) and dates beyond 3 months
                      return d.isBefore(today) || d.isAfter(threeMonthsFromNow);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!errors?.start_date,
                        helperText: errors?.start_date?.message,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            height: '44px',
                            borderColor: '#E5E7EB',
                            borderRadius: '6px',
                          },
                        },
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* End Date */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  fontSize: '14px',
                  marginBottom: 0.5,
                  paddingLeft: 0.5,
                  fontWeight: 500,
                  color: '#4A5264',
                }}
              >
                End Date
              </FormLabel>
              <Controller
                name='end_date'
                control={control}
                render={({ field }) => (
                  <DatePicker
                    key={`end-date-${watchedValues.start_date || 'no-start'}`} // Force re-render when start_date changes
                    value={field.value ? moment(field.value) : null}
                    onChange={(val) =>
                      field.onChange(
                        val ? (val.toDate?.() ? val.toDate() : val) : null
                      )
                    }
                    format='DD/MM/YYYY'
                    referenceDate={
                      watchedValues.start_date
                        ? (() => {
                            const startDate = moment(watchedValues.start_date);
                            const dayOfMonth = startDate.date();
                            const daysInMonth = startDate.daysInMonth();

                            // If start date is the very last day of month, open next month
                            // This prevents user from seeing mostly disabled dates
                            if (dayOfMonth === daysInMonth) {
                              const nextMonth = startDate
                                .clone()
                                .add(1, 'month')
                                .startOf('month');

                              return nextMonth;
                            }

                            return startDate;
                          })()
                        : moment() // Current month when no start date
                    }
                    shouldDisableDate={(day) => {
                      const startVal = watch('start_date');
                      const start = startVal
                        ? moment(startVal).startOf('day')
                        : null;
                      const d = day.startOf('day');
                      if (!start) {
                        // Without start date, still disallow today/past to match schema rule
                        const today = moment().startOf('day');
                        return !d.isAfter(today);
                      }
                      // Disable any date on/before start date
                      return !d.isAfter(start);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!errors?.end_date,
                        helperText: errors?.end_date?.message,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            height: '44px',
                            borderColor: '#E5E7EB',
                            borderRadius: '6px',
                          },
                        },
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Estimated Hours */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  fontSize: '14px',
                  marginBottom: 0.5,
                  paddingLeft: 0.5,
                  fontWeight: 500,
                  color: '#4A5264',
                }}
              >
                Estimated Hours
              </FormLabel>
              <Controller
                name='estimated_hours'
                control={control}
                render={({
                  field: { value, onChange: fieldOnChange, onBlur, ...field },
                }) => {
                  // Round to nearest upper step: 0.5 or 1.0
                  const roundToNearestUpperStep = (num) => {
                    if (num === '' || num === null || num === undefined) {
                      return num;
                    }

                    const numericValue = Number(num);
                    if (Number.isNaN(numericValue)) {
                      return num;
                    }

                    // Get integer and decimal parts
                    const integerPart = Math.floor(numericValue);
                    const decimalPart = numericValue - integerPart;

                    // Round decimal part to nearest upper step (0.5 or 1.0)
                    let roundedDecimal = 0;
                    if (decimalPart > 0) {
                      if (decimalPart <= 0.5) {
                        roundedDecimal = 0.5;
                      } else {
                        roundedDecimal = 1.0;
                      }
                    }

                    // If decimal rounds to 1.0, add 1 to integer and set decimal to 0
                    if (roundedDecimal >= 1.0) {
                      return integerPart + 1;
                    }

                    return integerPart + roundedDecimal;
                  };

                  return (
                    <NumberInput
                      {...field}
                      value={value}
                      onChange={(nextValue) => {
                        if (
                          nextValue === '' ||
                          nextValue === null ||
                          nextValue === undefined
                        ) {
                          fieldOnChange('');
                          return;
                        }

                        // Allow typing intermediate values, but round on blur
                        fieldOnChange(nextValue);
                      }}
                      onBlur={(event) => {
                        onBlur(event);
                        const raw = event?.target?.value;
                        if (raw === undefined || raw === null || raw === '') {
                          return;
                        }

                        const numericValue = Number(raw);
                        if (Number.isNaN(numericValue)) {
                          return;
                        }

                        // Round to nearest upper step
                        const rounded = roundToNearestUpperStep(numericValue);
                        fieldOnChange(rounded);
                        setValue('estimated_hours', rounded, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      placeholder='e.g. 120'
                      unit='hours'
                      inputProps={{ min: 0.5, step: 0.5 }}
                    />
                  );
                }}
              />
              {errors?.estimated_hours ? (
                <FormHelperText
                  sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5 }}
                >
                  {errors.estimated_hours.message}
                </FormHelperText>
              ) : (
                <FormHelperText
                  sx={{ color: '#6B7280', fontSize: '0.75rem', mt: 0.5 }}
                >
                  0.5 = half hour, 1 = one hour
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Duration */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  fontSize: '14px',
                  marginBottom: 0.5,
                  paddingLeft: 0.5,
                  fontWeight: 500,
                  color: '#4A5264',
                }}
              >
                Duration
              </FormLabel>
              <Controller
                name='duration'
                control={control}
                render={({ field }) => (
                  <OutlinedInput
                    value={field.value || ''}
                    placeholder='e.g. 2'
                    type='number'
                    onChange={(e) => {
                      const value =
                        e.target.value === '' ? null : Number(e.target.value);
                      field.onChange(value);
                    }}
                    endAdornment={
                      <InputAdornment position='end'>
                        <Controller
                          name='duration_unit'
                          control={control}
                          render={({ field: unitField }) => (
                            <NativeSelect
                              {...unitField}
                              input={<CustomNativeSelect textAlign='right' />}
                            >
                              <option value='days'>days</option>
                              <option value='weeks'>weeks</option>
                              <option value='months'>months</option>
                            </NativeSelect>
                          )}
                        />
                      </InputAdornment>
                    }
                    sx={{
                      height: '44px',
                      borderColor: '#E5E7EB',
                      borderRadius: '6px',
                    }}
                  />
                )}
              />
              {/* Field-specific errors */}
              {(errors?.duration || errors?.duration_unit) && (
                <FormHelperText sx={{ color: '#d32f2f', fontSize: '0.75rem' }}>
                  {errors.duration?.message || errors.duration_unit?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        {/* Milestone Amount Calculation Display */}
        {(milestoneAmounts.hours !== null ||
          milestoneAmounts.freelancerAmount !== null ||
          milestoneAmounts.clientAmount !== null) && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2.5,
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}
            >
              Milestone Amount Calculation
            </Typography>

            {/* First Row: Freelancer Hourly Rate, Markup Percent, FX Rate */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{ color: '#6B7280', fontSize: '0.75rem' }}
                  >
                    Freelancer Hourly Rate
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: 500, color: '#111827', mt: 0.5 }}
                  >
                    {formatCurrency(milestoneAmounts.hourlyRate)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{ color: '#6B7280', fontSize: '0.75rem' }}
                  >
                    Markup Percent
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: 500, color: '#111827', mt: 0.5 }}
                  >
                    {milestoneAmounts.markupPercent !== null &&
                    milestoneAmounts.markupPercent !== undefined
                      ? `${milestoneAmounts.markupPercent}%`
                      : '—'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{ color: '#6B7280', fontSize: '0.75rem' }}
                  >
                    FX Rate
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: 500, color: '#111827', mt: 0.5 }}
                  >
                    {milestoneAmounts.fxRate !== null &&
                    milestoneAmounts.fxRate !== undefined
                      ? milestoneAmounts.fxRate
                      : '—'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Second Row: Freelancer Payout, Billable Amount (Gross), Platform Revenue (Profit) */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{ color: '#6B7280', fontSize: '0.75rem' }}
                  >
                    Freelancer Payout
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: '#111827', mt: 0.5 }}
                  >
                    {formatCurrency(milestoneAmounts.freelancerAmount)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{ color: '#6B7280', fontSize: '0.75rem' }}
                  >
                    Billable Amount (Gross)
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: '#111827', mt: 0.5 }}
                  >
                    {formatCurrency(milestoneAmounts.clientAmount)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{ color: '#6B7280', fontSize: '0.75rem' }}
                  >
                    Platform Revenue (Profit)
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: '#111827', mt: 0.5 }}
                  >
                    {formatCurrency(milestoneAmounts.platformRevenue)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {(!milestoneAmounts.hourlyRate ||
              !milestoneAmounts.freelancerAmount ||
              !milestoneAmounts.clientAmount) && (
              <Typography
                variant='caption'
                sx={{
                  color: '#F59E0B',
                  fontSize: '0.75rem',
                  mt: 1.5,
                  display: 'block',
                }}
              >
                ⚠️ Some calculation data is missing. Please ensure SOW has
                freelancer rate and markup percent configured.
              </Typography>
            )}
          </Paper>
        )}

        {/* Submit Buttons */}
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}
        >
          <Button variant='outlined' onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading
              ? milestone
                ? 'Updating...'
                : 'Creating...'
              : milestone
              ? 'Update Milestone'
              : 'Create Milestone'}
          </Button>
        </Box>
      </form>

      <RevertToDraftDialog
        open={revertDialogOpen}
        title='Edit Milestones (Regenerate SOW)'
        description='Milestones can only be updated before the SOW is generated. To edit/delete them, you need to regenerate the SOW.'
        note='This action will delete all existing documents.'
        loading={revertLoading}
        onCancel={() => {
          setRevertDialogOpen(false);
          setPendingFormData(null);
        }}
        onProceed={handleRevertToDraft}
      />
      <SowAddendumRequiredDialog
        open={addendumRequiredOpen}
        onCancel={() => setAddendumRequiredOpen(false)}
        onProceed={() => {
          setAddendumRequiredOpen(false);
          setAddendumFormOpen(true);
        }}
      />
      <SowAddendumFormDialog
        open={addendumFormOpen}
        onClose={() => setAddendumFormOpen(false)}
      />
    </LocalizationProvider>
  );
};

export default MilestoneForm;
